import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Edit3, Eye, Box, Package } from 'lucide-react';
import Pagination from '../../components/Common/Pagination';
import { TableLoader, Spinner } from '../../components/Common/Loader';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import {
  getCrates,
  getCrateById,
  createCrate,
  updateCrate,
  deleteCrate,
  type ApiCrateItem,
  type ApiCratePrimaryProduct,
} from '../../api/productMasters';

export const CratesList: React.FC = () => {
  const [crates, setCrates] = useState<ApiCrateItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'add' | 'edit' | 'view'>('list');
  const [editingItem, setEditingItem] = useState<ApiCrateItem | null>(null);
  const [viewingItem, setViewingItem] = useState<ApiCrateItem | null>(null);
  const [detailProducts, setDetailProducts] = useState<ApiCratePrimaryProduct[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    units: '',
  });

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    try {
      if (dateStr.includes('T')) {
        const parts = dateStr.split('T');
        const time = parts[1].split('.')[0];
        return `${parts[0]} ${time}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Fetch Crates List
  const fetchCratesData = async () => {
    try {
      setLoading(true);
      const res = await getCrates();
      if (res && (res.status === 'success' || res.success) && res.data) {
        const rawList = Array.isArray(res.data)
          ? res.data
          : ((res.data as any)?.data || (res.data as any)?.items || []);
        if (Array.isArray(rawList)) {
          setCrates(rawList);
        }
      }
    } catch (err) {
      console.error('Error fetching crates list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCratesData();
  }, []);

  // Filtering Logic
  const filteredCrates = useMemo(() => {
    return crates.filter((item) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const matchName = item.name?.toLowerCase().includes(query);
      const matchId = String(item.id).includes(query);
      const matchUnits = String(item.units).includes(query);
      return matchName || matchId || matchUnits;
    });
  }, [crates, searchQuery]);

  // Paginated Items
  const paginatedCrates = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredCrates.slice(startIndex, startIndex + entriesLimit);
  }, [filteredCrates, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      units: '',
    });
    setView('add');
  };

  const handleOpenEdit = (item: ApiCrateItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      units: item.units != null ? String(item.units) : '',
    });
    setView('edit');
  };

  const handleOpenView = async (item: ApiCrateItem) => {
    setViewingItem(item);
    setDetailProducts(item.primaryProducts || []);
    setView('view');

    // Fetch detailed crate info including primaryProducts if available
    try {
      setLoadingDetails(true);
      const res = await getCrateById(item.id);
      const data = res?.data || res;
      if (data) {
        if (data.primaryProducts && Array.isArray(data.primaryProducts)) {
          setDetailProducts(data.primaryProducts);
        }
        if (data.name) {
          setViewingItem(prev => (prev ? { ...prev, ...data } : data));
        }
      }
    } catch (err) {
      console.error('Error fetching crate details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDelete = async (id: number | string) => {
    if (!window.confirm('Are you sure you want to delete this crate?')) return;
    try {
      await deleteCrate(id);
      setCrates(prev => prev.filter(c => c.id !== Number(id)));
      if (view === 'view') setView('list');
    } catch (err: any) {
      console.error('Error deleting crate:', err);
      alert(err.message || 'Failed to delete crate. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Crate Name is required.');
      return;
    }
    const unitsNum = Number(formData.units);
    if (isNaN(unitsNum) || unitsNum <= 0) {
      alert('Please enter a valid positive number for units.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        units: unitsNum,
      };

      if (editingItem) {
        await updateCrate(editingItem.id, payload);
      } else {
        await createCrate(payload);
      }

      await fetchCratesData();
      setView('list');
    } catch (err: any) {
      console.error('Error saving crate:', err);
      alert(err.message || 'Failed to save crate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Crate Name', 'Units Capacity', 'Created At', 'Updated At'];
    const rows = filteredCrates.map((c) => [
      c.id,
      c.name,
      c.units,
      formatDate(c.createdAt),
      formatDate(c.updatedAt),
    ]);
    exportToCSV('crates_list', headers, rows);
  };

  // Render View Details Page
  if (view === 'view' && viewingItem) {
    return (
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setView('list')}
              className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
              title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
                {viewingItem.name}
              </h1>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => handleOpenEdit(viewingItem)}
              className="bg-[#F9C327] hover:bg-[#E5B21E] text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => handleDelete(viewingItem.id)}
              className="bg-[#4E342E] hover:bg-[#3E2723] text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-6">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Box className="w-5 h-5 text-[#C1272D]" />
            <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">
              Basic Crate Information
            </h3>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50/70 p-4 rounded-2xl border border-slate-100">
            <div>
              <span className="text-slate-400 text-xs font-medium block">Crate Name</span>
              <span className="text-sm font-bold text-slate-800">{viewingItem.name}</span>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium block">Units Capacity</span>
              <span className="text-sm font-extrabold text-[#C1272D]">{viewingItem.units} Units</span>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium block">Created At</span>
              <span className="text-xs font-semibold text-slate-700">{formatDate(viewingItem.createdAt)}</span>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium block">Updated At</span>
              <span className="text-xs font-semibold text-slate-700">{formatDate(viewingItem.updatedAt)}</span>
            </div>
          </div>

          {/* Primary Products Section */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <Package className="w-5 h-5 text-[#C1272D]" />
              <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">
                Associated Products ({detailProducts.length})
              </h3>
            </div>

            {loadingDetails ? (
              <div className="py-6 text-center text-xs font-semibold text-slate-500">
                <Spinner size="md" color="text-[#C1272D]" />
                <p className="mt-2">Loading associated products...</p>
              </div>
            ) : detailProducts.length === 0 ? (
              <div className="p-4 text-center text-xs font-semibold text-slate-400 bg-slate-50 rounded-2xl border border-slate-100">
                No primary products linked to this crate unit.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-[#FFF8E1] text-slate-800 font-bold uppercase text-[10px] tracking-wider">
                      <th className="py-3 px-4">Product ID</th>
                      <th className="py-3 px-4">Product Name</th>
                      <th className="py-3 px-4">SAP Code</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {detailProducts.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4 font-bold text-slate-700">{p.id}</td>
                        <td className="py-3 px-4 font-extrabold text-slate-900">{p.name}</td>
                        <td className="py-3 px-4 font-semibold text-slate-600">{p.sapCode || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render Add / Edit View
  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setView('list')}
              className="w-7 h-7 rounded-full go_erp-bg transition-all flex items-center justify-center text-white cursor-pointer"
              title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h3 className="text-xl font-bold go_erp-text tracking-wide">
              {view === 'edit' ? 'Edit Crate' : 'Add Crate'}
            </h3>
          </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Crate Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">
                Crate Name*
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Milk Crate 12 Pouch"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white"
              />
            </div>

            {/* Units */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">
                Units Capacity (Pcs/Pouches)*
              </label>
              <input
                type="number"
                required
                min={1}
                placeholder="e.g. 12"
                value={formData.units}
                onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Spinner size="sm" color="text-white" />}
              {submitting ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Render List View
  return (
    <div className="space-y-6">
      {/* Control Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Crates Management
          </h1>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Crate
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts Header */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>
          Total Crates: <span className="font-extrabold">{crates.length}</span>
        </span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search crates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-48 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Crate Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Units Capacity</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Created At</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={5} text="Loading crates..." />
              ) : paginatedCrates.length > 0 ? (
                paginatedCrates.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282] font-bold">{item.id}</td>
                    <td className="px-4 py-4 text-xs text-slate-800 font-extrabold">{item.name}</td>
                    <td className="px-4 py-4 text-xs font-bold text-[#C1272D]">
                      {item.units} Units
                    </td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-4 text-xs text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenView(item)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(item)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                          title="Edit Crate"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-slate-500 hover:text-rose-600 transition-colors cursor-pointer rounded-lg"
                          title="Delete Crate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No crates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredCrates.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default CratesList;
